import React from 'react';
import {useDispatch} from 'react-redux';
import {WeatherService} from '../api';
import {WeatherWarningListResponse} from '../api/types';
import {pushStationWeatherWarnings} from '../store/weatherData';
import {getStationWeatherWarnings} from '../store/weatherData/selectors';
import {Dates} from '../utils';
import useApiClient from './useApiClient';
import useParamSelector from './useParamSelector';

const useStationWarning = (stationId: number, lat: number, lon: number) => {
  const {fetch} = useApiClient();
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = React.useState(false);

  const warningsData: WeatherWarningListResponse = useParamSelector(
    getStationWeatherWarnings,
    stationId,
  );

  const isWarningUpdateRequired = React.useCallback(() => {
    if (
      warningsData.computed_at === null ||
      Dates.timeDiffUtcWithNowInMin(warningsData.computed_at) > 60
    ) {
      return true;
    }
    return false;
  }, [warningsData]);

  const updateStationWarnings = React.useCallback(async () => {
    if (isUpdating || !stationId || !lat || !lon) {
      return; // Prevent multiple simultaneous calls or invalid params
    }

    setIsUpdating(true);
    try {
      const warningListResponse: WeatherWarningListResponse = await fetch(
        WeatherService.locationWeatherWarnings({
          lat,
          lon,
        }),
        {ignore404: true},
      );

      dispatch(
        pushStationWeatherWarnings({
          stationId,
          warningListResponse,
        }),
      );
    } catch {
      // Handle error silently as before
    } finally {
      setIsUpdating(false);
    }
  }, [stationId, lat, lon, fetch, dispatch, isUpdating]);

  const memoizedReturn = React.useMemo(
    () => ({
      updateStationWarnings,
      warnings: warningsData.warnings || [],
      isWarningUpdateRequired,
      isUpdating,
    }),
    [
      updateStationWarnings,
      warningsData.warnings,
      isWarningUpdateRequired,
      isUpdating,
    ],
  );

  return memoizedReturn;
};

export default useStationWarning;
